// components/AlumniManagement.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../components/auth/AuthContext";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Divider,
  Alert,
   Groups as AlumniIcon,
} from "@mui/material";
import {
  Edit,
  Visibility,
  School,
  FilterList,
  Download,
  Add,
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom"; 

const AlumniManagement = () => {
  const { schoolId, schoolName } = useAuth();
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alumni, setAlumni] = useState([]);
  const [filteredAlumni, setFilteredAlumni] = useState([]);
  const [graduationYears, setGraduationYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

  // Fetch alumni data
  useEffect(() => {
    fetchAlumni();
  }, [schoolId]);

  const fetchAlumni = async () => {
    try {
      setLoading(true);
      const response = await axios.get("https://kidsm.vercel.app/api/schools/alumni", {
        params: { schoolId }
      });

      if (response.data.success) {
        setAlumni(response.data.data.alumni);
        setFilteredAlumni(response.data.data.alumni);
        
        // Extract unique graduation years
        const years = [...new Set(response.data.data.alumni.map(a => a.graduationYear))].sort((a, b) => b - a);
        setGraduationYears(years);
      }
    } catch (error) {
      console.error("Error fetching alumni:", error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = alumni;

    if (selectedYear) {
      filtered = filtered.filter(a => a.graduationYear === selectedYear);
    }

    if (selectedClass) {
      filtered = filtered.filter(a => a.graduatingClass === selectedClass);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.fullName.toLowerCase().includes(query) ||
        a.admissionNumber.toLowerCase().includes(query) ||
        (a.furtherEducation?.institution?.toLowerCase() || '').includes(query) ||
        (a.currentOccupation?.company?.toLowerCase() || '').includes(query)
      );
    }

    setFilteredAlumni(filtered);
  }, [selectedYear, selectedClass, searchQuery, alumni]);

  const handleEditAlumni = (alumniData) => {
    setSelectedAlumni(alumniData);
    setEditData({
      furtherEducation: alumniData.furtherEducation || {},
      currentOccupation: alumniData.currentOccupation || {},
      contactInformation: alumniData.contactInformation || {},
      achievements: alumniData.achievements || [],
      careerPathway: alumniData.careerPathway || "",
      remarks: alumniData.remarks || ""
    });
    setEditDialog(true);
  };

  const handleSaveAlumni = async () => {
    try {
      setSaving(true);
      const response = await axios.put(
        `https://kidsm.vercel.app/api/schools/alumni/${selectedAlumni._id}`,
        editData
      );

      if (response.data.success) {
        // Update local state
        const updatedAlumni = alumni.map(a => 
          a._id === selectedAlumni._id ? { ...a, ...editData, updatedAt: new Date() } : a
        );
        
        setAlumni(updatedAlumni);
        setEditDialog(false);
        setSelectedAlumni(null);
        
        alert("Alumni information updated successfully!");
      }
    } catch (error) {
      console.error("Error updating alumni:", error);
      alert("Failed to update alumni information");
    } finally {
      setSaving(false);
    }
  };

 const renderAlumniCard = (alumniData) => {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {alumniData.fullName}
          </Typography>
          <Chip 
            label={`Class of ${alumniData.graduationYear}`} 
            size="small" 
            color="primary" 
          />
        </Box>
        
        <Typography color="textSecondary" gutterBottom>
          {alumniData.graduatingClass} • {alumniData.stream || 'N/A'}
        </Typography>
        
        <Divider sx={{ my: 1 }} />
        
        {alumniData.furtherEducation?.institution && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Further Education:
            </Typography>
            <Typography variant="body2">
              {alumniData.furtherEducation.institution} - {alumniData.furtherEducation.course}
            </Typography>
          </Box>
        )}
        
        {alumniData.currentOccupation?.company && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Current Occupation:
            </Typography>
            <Typography variant="body2">
              {alumniData.currentOccupation.position} at {alumniData.currentOccupation.company}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={() => navigate("/alumni-details", { state: { alumni: alumniData } })}
          >
            View Details
          </Button>
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={() => handleEditAlumni(alumniData)}
            variant="outlined"
          >
            Edit
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Alumni Management
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Manage and track alumni information
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => {
              // Export alumni data
              alert("Export feature coming soon!");
            }}
          >
            Export Alumni
          </Button>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search alumni..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Graduation Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                label="Graduation Year"
              >
                <MenuItem value="">All Years</MenuItem>
                {graduationYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Graduating Class</InputLabel>
              <Select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                label="Graduating Class"
              >
                <MenuItem value="">All Classes</MenuItem>
                <MenuItem value="Grade 9">Grade 9</MenuItem>
                <MenuItem value="Grade 12">Grade 12</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => {
                setSelectedYear("");
                setSelectedClass("");
                setSearchQuery("");
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredAlumni.length === 0 ? (
          <Alert severity="info">
            No alumni found matching your criteria
          </Alert>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              {filteredAlumni.length} Alumni Found
            </Typography>
            
            <Grid container spacing={2}>
              {filteredAlumni.map((alumniData) => (
                <Grid item xs={12} md={6} lg={4} key={alumniData._id}>
                  {renderAlumniCard(alumniData)}
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Paper>

      {/* Edit Alumni Dialog */}
      <Dialog
        open={editDialog}
        onClose={() => {
          if (!saving) {
            setEditDialog(false);
            setSelectedAlumni(null);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Alumni Information - {selectedAlumni?.fullName}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Further Education
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Institution"
                value={editData.furtherEducation?.institution || ""}
                onChange={(e) => setEditData({
                  ...editData,
                  furtherEducation: {
                    ...editData.furtherEducation,
                    institution: e.target.value
                  }
                })}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Course"
                value={editData.furtherEducation?.course || ""}
                onChange={(e) => setEditData({
                  ...editData,
                  furtherEducation: {
                    ...editData.furtherEducation,
                    course: e.target.value
                  }
                })}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Current Occupation
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company"
                value={editData.currentOccupation?.company || ""}
                onChange={(e) => setEditData({
                  ...editData,
                  currentOccupation: {
                    ...editData.currentOccupation,
                    company: e.target.value
                  }
                })}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Position"
                value={editData.currentOccupation?.position || ""}
                onChange={(e) => setEditData({
                  ...editData,
                  currentOccupation: {
                    ...editData.currentOccupation,
                    position: e.target.value
                  }
                })}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Career Pathway</InputLabel>
                <Select
                  value={editData.careerPathway || ""}
                  onChange={(e) => setEditData({
                    ...editData,
                    careerPathway: e.target.value
                  })}
                  label="Career Pathway"
                >
                  <MenuItem value="">Select Pathway</MenuItem>
                  <MenuItem value="STEM">STEM</MenuItem>
                  <MenuItem value="Arts & Humanities">Arts & Humanities</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                  <MenuItem value="Technical">Technical</MenuItem>
                  <MenuItem value="Creative Arts">Creative Arts</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                value={editData.remarks || ""}
                onChange={(e) => setEditData({
                  ...editData,
                  remarks: e.target.value
                })}
                margin="normal"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialog(false);
              setSelectedAlumni(null);
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAlumni}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AlumniManagement;